"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useLayoutEffect, useRef, useState } from "react";
import { ArrowLeftIcon, CheckIcon, ChevronDoubleLeftIcon, ChevronDownIcon, LinkIcon, MagnifyingGlassIcon, ShareIcon } from "@heroicons/react/24/outline";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import type { SetActiveContentTab } from "@/components/brain/ContentTabContext";
import { useWaveChatScrollOptional } from "@/contexts/wave/WaveChatScrollContext";
import type { ApiWave } from "@/generated/models/ApiWave";
import { getWaveHomeRoute } from "@/helpers/navigation.helpers";
import { getWaveDescriptionPreviewText } from "@/helpers/waves/waveDescriptionPreview";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useWaveShareCopyAction } from "@/hooks/waves/useWaveShareCopyAction";
import { MyStreamWaveTab } from "@/types/waves.types";
import WaveDropsSearchModal from "@/components/waves/drops/search/WaveDropsSearchModal";
import WaveDescriptionPopover from "@/components/waves/header/WaveDescriptionPopover";
import WavePicture from "../../../waves/WavePicture";
import MyStreamActionTooltip from "../MyStreamActionTooltip";
import { useSidebarState } from "../../../../hooks/useSidebarState";
const TRUNCATION_EPSILON_PX = 1;
export interface MyStreamWaveTabsHeaderActionContext {
  readonly activeContentTab: MyStreamWaveTab;
  readonly isCompact: boolean;
  readonly tooltipId: string;
}
interface MyStreamWaveTabsHeaderProps {
  readonly wave: ApiWave;
  readonly activeContentTab: MyStreamWaveTab;
  readonly setActiveContentTab: SetActiveContentTab;
  readonly onSelectCuration: (curationId: string | null) => void;
  readonly isCompact: boolean;
  readonly showBackButton: boolean;
  readonly headerActionsTooltipId: string;
  readonly headerClassName: string;
  readonly actionsClassName: string;
  readonly rightSidebarButtonBackgroundClassName?: string | undefined;
  readonly renderLeadingActions?: ((context: MyStreamWaveTabsHeaderActionContext) => React.ReactNode) | undefined;
}
export default function MyStreamWaveTabsHeader(t0) {
  const $ = _c(91);
  const {
    wave,
    activeContentTab,
    setActiveContentTab,
    onSelectCuration,
    isCompact,
    showBackButton,
    headerActionsTooltipId,
    headerClassName,
    actionsClassName,
    rightSidebarButtonBackgroundClassName: t1,
    renderLeadingActions
  } = t0;
  const rightSidebarButtonBackgroundClassName = t1 === undefined ? "tw-bg-iron-800" : t1;
  const {
    toggleRightSidebar,
    isRightSidebarOpen
  } = useSidebarState();
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const {
    isApp
  } = useDeviceInfo();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const descriptionPreviewRef = useRef(null);
  const [isDescriptionPreviewTruncated, setIsDescriptionPreviewTruncated] = useState(false);
  const waveChatScroll = useWaveChatScrollOptional();
  const isDirectMessage = wave.chat.scope.group?.is_direct_message ?? false;
  const showShareAction = !isDirectMessage;
  let t2;
  if ($[0] !== wave) {
    t2 = getWaveDescriptionPreviewText(wave);
    $[0] = wave;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const previewText = t2;
  const showDescriptionPreview = showShareAction && !!previewText;
  let t3;
  if ($[2] !== isDirectMessage || $[3] !== wave.id || $[4] !== wave.name) {
    t3 = {
      waveId: wave.id,
      waveName: wave.name,
      isDirectMessage
    };
    $[2] = isDirectMessage;
    $[3] = wave.id;
    $[4] = wave.name;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    mode: waveLinkActionMode,
    label: waveLinkActionLabel,
    feedbackState: waveLinkActionFeedbackState,
    onClick: handleWaveLinkActionClick
  } = useWaveShareCopyAction(t3);
  let t4;
  if ($[6] !== isApp || $[7] !== isDirectMessage || $[8] !== router || $[9] !== searchParams) {
    t4 = () => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("wave");
      params.delete("serialNo");
      params.delete("divider");
      params.delete("drop");
      params.delete("curation");
      const basePath = getWaveHomeRoute({
        isDirectMessage,
        isApp
      });
      const newUrl = params.toString() ? `${basePath}?${params.toString()}` : basePath;
      router.push(newUrl, {
        scroll: false
      });
    };
    $[6] = isApp;
    $[7] = isDirectMessage;
    $[8] = router;
    $[9] = searchParams;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleMobileBack = t4;
  let t5;
  if ($[11] !== onSelectCuration || $[12] !== pathname || $[13] !== router || $[14] !== searchParams || $[15] !== setActiveContentTab || $[16] !== wave.id || $[17] !== waveChatScroll) {
    t5 = serialNo => {
      onSelectCuration(null);
      setActiveContentTab(MyStreamWaveTab.CHAT);
      if (waveChatScroll) {
        waveChatScroll.requestScrollToSerialNo({
          waveId: wave.id,
          serialNo
        });
        return;
      }
      const params_0 = new URLSearchParams(searchParams.toString() || "");
      params_0.delete("curation");
      params_0.set("serialNo", String(serialNo));
      router.replace(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[11] = onSelectCuration;
    $[12] = pathname;
    $[13] = router;
    $[14] = searchParams;
    $[15] = setActiveContentTab;
    $[16] = wave.id;
    $[17] = waveChatScroll;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const handleSearchSelect = t5;
  const waveLinkActionIconColor = waveLinkActionFeedbackState === "idle" ? "tw-text-iron-200" : "tw-text-emerald-300";
  const rightSidebarActionLabel = isRightSidebarOpen ? "Hide right sidebar" : "Show right sidebar";
  let t6;
  if ($[19] !== waveLinkActionFeedbackState || $[20] !== waveLinkActionMode) {
    t6 = () => {
      if (waveLinkActionFeedbackState !== "idle") {
        return <CheckIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
      }
      if (waveLinkActionMode === "share") {
        return <ShareIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
      }
      return <LinkIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
    };
    $[19] = waveLinkActionFeedbackState;
    $[20] = waveLinkActionMode;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const renderWaveLinkActionIcon = t6;
  let t7;
  if ($[22] !== showDescriptionPreview) {
    t7 = () => {
      if (!showDescriptionPreview) {
        const frameId = globalThis.requestAnimationFrame(() => {
          setIsDescriptionPreviewTruncated(false);
        });
        return () => {
          globalThis.cancelAnimationFrame(frameId);
        };
      }
      const previewElement = descriptionPreviewRef.current;
      if (!previewElement) {
        return;
      }
      const updateTruncationState = () => {
        setIsDescriptionPreviewTruncated(previewElement.scrollWidth > previewElement.clientWidth + TRUNCATION_EPSILON_PX);
      };
      const frameId_0 = globalThis.requestAnimationFrame(updateTruncationState);
      if (typeof ResizeObserver === "undefined") {
        globalThis.addEventListener("resize", updateTruncationState);
        return () => {
          globalThis.cancelAnimationFrame(frameId_0);
          globalThis.removeEventListener("resize", updateTruncationState);
        };
      }
      const observer = new ResizeObserver(updateTruncationState);
      observer.observe(previewElement);
      if (previewElement.parentElement) {
        observer.observe(previewElement.parentElement);
      }
      return () => {
        globalThis.cancelAnimationFrame(frameId_0);
        observer.disconnect();
      };
    };
    $[22] = showDescriptionPreview;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== previewText || $[25] !== showDescriptionPreview) {
    t8 = [previewText, showDescriptionPreview];
    $[24] = previewText;
    $[25] = showDescriptionPreview;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  useLayoutEffect(t7, t8);
  let t9;
  if ($[27] !== handleMobileBack || $[28] !== showBackButton) {
    t9 = showBackButton && <button type="button" onClick={handleMobileBack} className="-tw-ml-2.5 tw-mr-1.5 tw-flex tw-h-full tw-items-center tw-border-0 tw-bg-transparent tw-p-0 tw-px-2.5 tw-text-iron-300 tw-transition-colors hover:tw-text-iron-50" aria-label="Go back"><ArrowLeftIcon className="tw-h-6 tw-w-6 tw-flex-shrink-0" /></button>;
    $[27] = handleMobileBack;
    $[28] = showBackButton;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const t10 = wave.name;
  const t11 = wave.picture;
  let t12;
  if ($[30] !== wave.contributors_overview) {
    t12 = wave.contributors_overview.map(_temp);
    $[30] = wave.contributors_overview;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t12 || $[33] !== wave.name || $[34] !== wave.picture) {
    t13 = <div className="tw-size-9 tw-flex-shrink-0 tw-rounded-full tw-ring-1 tw-ring-white/30 tw-ring-offset-1 tw-ring-offset-iron-950"><WavePicture name={t10} picture={t11} contributors={t12} /></div>;
    $[32] = t12;
    $[33] = wave.name;
    $[34] = wave.picture;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== isDescriptionPreviewTruncated || $[37] !== previewText || $[38] !== showDescriptionPreview || $[39] !== wave) {
    t14 = showDescriptionPreview ? <WaveDescriptionPopover wave={wave} align="left" ariaLabel="Show wave description" triggerClassName="tw-group tw-flex tw-min-w-0 tw-cursor-pointer tw-flex-col tw-items-start tw-border-0 tw-bg-transparent tw-p-0 tw-text-left"><h1 className="tw-mb-0 tw-w-full tw-truncate tw-text-sm tw-font-semibold tw-tracking-tight tw-text-white/95 lg:tw-text-xl">{wave.name}</h1><span className="tw-mt-0.5 tw-flex tw-w-full tw-min-w-0 tw-max-w-xl tw-items-center tw-gap-x-1.5"><span ref={descriptionPreviewRef} className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-xs tw-font-normal tw-text-iron-400 tw-transition-colors tw-duration-300 group-hover:tw-text-iron-300">{previewText}</span>{isDescriptionPreviewTruncated && <ChevronDownIcon aria-hidden="true" className="tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-300 tw-transition-colors group-hover:tw-text-white" />}</span></WaveDescriptionPopover> : <h1 className="tw-mb-0 tw-truncate tw-text-sm tw-font-semibold tw-tracking-tight tw-text-white/95 lg:tw-text-xl">{wave.name}</h1>;
    $[36] = isDescriptionPreviewTruncated;
    $[37] = previewText;
    $[38] = showDescriptionPreview;
    $[39] = wave;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== t13 || $[42] !== t14 || $[43] !== t9) {
    t15 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-x-3">{t9}{t13}{t14}</div>;
    $[41] = t13;
    $[42] = t14;
    $[43] = t9;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== activeContentTab || $[46] !== headerActionsTooltipId || $[47] !== isCompact || $[48] !== renderLeadingActions) {
    t16 = renderLeadingActions?.({
      activeContentTab,
      isCompact,
      tooltipId: headerActionsTooltipId
    });
    $[45] = activeContentTab;
    $[46] = headerActionsTooltipId;
    $[47] = isCompact;
    $[48] = renderLeadingActions;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== handleWaveLinkActionClick || $[51] !== headerActionsTooltipId || $[52] !== renderWaveLinkActionIcon || $[53] !== showShareAction || $[54] !== waveLinkActionIconColor || $[55] !== waveLinkActionLabel || $[56] !== waveLinkActionMode) {
    t17 = showShareAction && <button type="button" onClick={handleWaveLinkActionClick} aria-label={waveLinkActionLabel} data-tooltip-id={headerActionsTooltipId} data-tooltip-content={waveLinkActionLabel} data-wave-link-action-mode={waveLinkActionMode} className={`tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white ${waveLinkActionIconColor}`}>{renderWaveLinkActionIcon()}</button>;
    $[50] = handleWaveLinkActionClick;
    $[51] = headerActionsTooltipId;
    $[52] = renderWaveLinkActionIcon;
    $[53] = showShareAction;
    $[54] = waveLinkActionIconColor;
    $[55] = waveLinkActionLabel;
    $[56] = waveLinkActionMode;
    $[57] = t17;
  } else {
    t17 = $[57];
  }
  let t18;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => setIsSearchOpen(true);
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  let t19;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <MagnifyingGlassIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  let t20;
  if ($[60] !== headerActionsTooltipId) {
    t20 = <button type="button" onClick={t18} aria-label="Search messages in this wave" data-tooltip-id={headerActionsTooltipId} data-tooltip-content="Search messages in this wave" className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-text-iron-200 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white">{t19}</button>;
    $[60] = headerActionsTooltipId;
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  const t21 = `tw-group tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 ${rightSidebarButtonBackgroundClassName} tw-shadow-[0_12px_28px_rgba(0,0,0,0.35)] tw-backdrop-blur-sm tw-transition tw-duration-300 tw-ease-out desktop-hover:hover:tw-border-iron-500/80 desktop-hover:hover:tw-bg-iron-700/85 desktop-hover:hover:tw-shadow-[0_16px_34px_rgba(0,0,0,0.4)]`;
  const t22 = `tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-200 tw-transition tw-duration-300 ${isRightSidebarOpen ? "tw-rotate-180 desktop-hover:group-hover:tw-translate-x-0.5" : "tw-rotate-0 desktop-hover:group-hover:-tw-translate-x-0.5"}`;
  let t23;
  if ($[62] !== t22) {
    t23 = <ChevronDoubleLeftIcon strokeWidth={2} className={t22} />;
    $[62] = t22;
    $[63] = t23;
  } else {
    t23 = $[63];
  }
  let t24;
  if ($[64] !== headerActionsTooltipId || $[65] !== rightSidebarActionLabel || $[66] !== t21 || $[67] !== t23 || $[68] !== toggleRightSidebar) {
    t24 = <button type="button" onClick={toggleRightSidebar} data-tooltip-id={headerActionsTooltipId} data-tooltip-content={rightSidebarActionLabel} className={t21} aria-label={rightSidebarActionLabel}>{t23}</button>;
    $[64] = headerActionsTooltipId;
    $[65] = rightSidebarActionLabel;
    $[66] = t21;
    $[67] = t23;
    $[68] = toggleRightSidebar;
    $[69] = t24;
  } else {
    t24 = $[69];
  }
  let t25;
  if ($[70] !== actionsClassName || $[71] !== t16 || $[72] !== t17 || $[73] !== t20 || $[74] !== t24) {
    t25 = <div className={actionsClassName}>{t16}{t17}{t20}{t24}</div>;
    $[70] = actionsClassName;
    $[71] = t16;
    $[72] = t17;
    $[73] = t20;
    $[74] = t24;
    $[75] = t25;
  } else {
    t25 = $[75];
  }
  let t26;
  if ($[76] !== headerClassName || $[77] !== t15 || $[78] !== t25) {
    t26 = <div className={headerClassName}>{t15}{t25}</div>;
    $[76] = headerClassName;
    $[77] = t15;
    $[78] = t25;
    $[79] = t26;
  } else {
    t26 = $[79];
  }
  let t27;
  if ($[80] !== headerActionsTooltipId) {
    t27 = <MyStreamActionTooltip id={headerActionsTooltipId} />;
    $[80] = headerActionsTooltipId;
    $[81] = t27;
  } else {
    t27 = $[81];
  }
  let t28;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = () => setIsSearchOpen(false);
    $[82] = t28;
  } else {
    t28 = $[82];
  }
  let t29;
  if ($[83] !== handleSearchSelect || $[84] !== isSearchOpen || $[85] !== wave) {
    t29 = <WaveDropsSearchModal isOpen={isSearchOpen} onClose={t28} wave={wave} onSelectSerialNo={handleSearchSelect} />;
    $[83] = handleSearchSelect;
    $[84] = isSearchOpen;
    $[85] = wave;
    $[86] = t29;
  } else {
    t29 = $[86];
  }
  let t30;
  if ($[87] !== t26 || $[88] !== t27 || $[89] !== t29) {
    t30 = <>{t26}{t27}{t29}</>;
    $[87] = t26;
    $[88] = t27;
    $[89] = t29;
    $[90] = t30;
  } else {
    t30 = $[90];
  }
  return t30;
}
function _temp(c) {
  return {
    pfp: c.contributor_pfp,
    identity: c.contributor_identity
  };
}